import styled from 'styled-components';
import type { FleetGroup } from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import {
  FleetChip,
  GroupUnitChip,
  ScrollRegion,
  fleetLeading,
  fleetType,
  formatCoef,
  formatCount,
  formatShare,
  groupUnitLabel,
  nameKey,
} from '@entities/fleet';

/**
 * Список групп. Широкий контейнер — таблица; узкий (телефон) — строки-карточки:
 * название и число моделей, ниже учёт, коэффициент и простой, ещё ниже —
 * должности Хронума. Переключение — container query, без JS.
 */

const NARROW = '640px';
/** Сколько должностей видно в строке; остальные — «ещё N». */
const POSITIONS_SHOWN = 2;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  thead th {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: left;
    color: ${({ theme }) => theme.colors.text.secondary};
    vertical-align: bottom;
  }

  thead th.num {
    text-align: right;
  }

  td {
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
    color: ${({ theme }) => theme.colors.text.primary};
    transition: background ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut};
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.c-name {
    min-width: 220px;
  }

  td.c-positions {
    min-width: 200px;
  }

  td.na {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  tr.row {
    cursor: pointer;
  }

  tr.row:hover > td,
  tr.row:focus-within > td {
    background: ${({ theme }) => theme.colors.surface};
  }

  .narrow-only {
    display: none;
  }

  @container groups (max-width: ${NARROW}) {
    &,
    & > tbody,
    & tr,
    & td {
      display: block;
    }

    & > thead,
    .wide-only {
      display: none;
    }

    .narrow-only {
      display: block;
    }

    tr.row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      column-gap: ${({ theme }) => theme.spacing(3)};
      row-gap: ${({ theme }) => theme.spacing(1.5)};
      padding: ${({ theme }) => `${theme.spacing(3)} 0`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    tr.row > td {
      padding: 0;
      border: none;
      min-width: 0;
      background: none;
    }

    tr.row:hover,
    tr.row:focus-within {
      background: ${({ theme }) => theme.colors.surface};
    }

    td.c-name {
      grid-column: 1;
      grid-row: 1;
    }

    td.c-models {
      grid-column: 2;
      grid-row: 1;
    }

    td.c-meta {
      grid-column: 1 / -1;
      grid-row: 2;
    }

    td.c-positions {
      grid-column: 1 / -1;
      grid-row: 3;
    }
  }
`;

const NameButton = styled.button`
  display: block;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fleetType.base};
  font-weight: 500;
  line-height: ${fleetLeading.tight};
  text-align: left;
  text-wrap: pretty;
  overflow-wrap: anywhere;
  cursor: pointer;
  transition: color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  tr.row:hover & {
    color: ${({ theme }) => theme.colors.primary};
  }

  tr[data-archived='true'] & {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Sub = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const Chips = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};

  td.c-name > & {
    margin-top: ${({ theme }) => theme.spacing(1.5)};
  }

  &:empty {
    display: none;
  }
`;

/** Должность Хронума — плашка с названием; ключ — в подсказке. */
const PositionTag = styled.span`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  min-height: 22px;
  padding: ${({ theme }) => `1px ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${fleetType.small};
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const More = styled.span`
  align-self: center;
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

const Meta = styled.span`
  display: block;
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const ModelsCount = styled.span`
  display: block;
  font-size: ${fleetType.base};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;

  & > .u {
    display: block;
    font-size: ${fleetType.micro};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

function Positions({ group }: { group: FleetGroup }) {
  const shown = group.positions.slice(0, POSITIONS_SHOWN);
  const rest = group.positions.length - shown.length;
  if (group.positions.length === 0) {
    return group.unit === 'day' ? (
      <Meta>не нужны — учёт в сутках</Meta>
    ) : (
      <Chips>
        <FleetChip
          $tone="warning"
          title="Без должности Хронума часы механизаторов в группу не попадут"
        >
          не заданы
        </FleetChip>
      </Chips>
    );
  }
  return (
    <Chips>
      {shown.map((p) => (
        <PositionTag key={p.key} title={`Ключ в Хронуме: ${p.key}`}>
          {p.name}
        </PositionTag>
      ))}
      {rest > 0 && <More>ещё {rest}</More>}
    </Chips>
  );
}

function GroupRow({ group, onOpen }: { group: FleetGroup; onOpen: (id: string) => void }) {
  const day = group.unit === 'day';
  const passport =
    group.operator_position && nameKey(group.operator_position) !== nameKey(group.name)
      ? group.operator_position
      : null;
  const meta = [
    groupUnitLabel(group.unit),
    day ? null : `коэф. ${formatCoef(group.hours_coef)}`,
    `простой ${formatShare(group.downtime_share)}`,
    `${formatCount(group.units_count)} ед.`,
  ]
    .filter((s): s is string => s !== null)
    .join(' · ');
  return (
    <tr
      className="row"
      data-archived={group.archived ? 'true' : undefined}
      onClick={() => {
        if (window.getSelection()?.toString()) return;
        onOpen(group.id);
      }}
    >
      <td className="c-name">
        <NameButton
          type="button"
          data-group-open={group.id}
          onClick={(e) => {
            e.stopPropagation();
            onOpen(group.id);
          }}
        >
          {group.name}
        </NameButton>
        {passport && <Sub>по должности в паспорте «{passport}»</Sub>}
        <Chips>{group.archived && <FleetChip $tone="muted">в архиве</FleetChip>}</Chips>
      </td>
      <td className="wide-only">
        <GroupUnitChip unit={group.unit} />
      </td>
      <td className={`wide-only num${day ? ' na' : ''}`}>
        {day ? 'не применяется' : formatCoef(group.hours_coef)}
      </td>
      <td className="wide-only num">{formatShare(group.downtime_share)}</td>
      <td className="num c-models">
        <span className="wide-only">{formatCount(group.models_count)}</span>
        <ModelsCount className="narrow-only">
          {formatCount(group.models_count)}
          <span className="u">
            {pluralizeRu(group.models_count, ['модель', 'модели', 'моделей'])}
          </span>
        </ModelsCount>
      </td>
      <td className="wide-only num">{formatCount(group.units_count)}</td>
      <td className="c-positions">
        <Positions group={group} />
      </td>
      <td className="narrow-only c-meta">
        <Meta>{meta}</Meta>
      </td>
    </tr>
  );
}

export function GroupTable({
  groups,
  onOpen,
}: {
  groups: readonly FleetGroup[];
  onOpen: (id: string) => void;
}) {
  return (
    <ScrollRegion label="Таблица групп механизаторов">
      <Table>
        <thead>
          <tr>
            <th scope="col">Группа</th>
            <th scope="col">Учёт</th>
            <th scope="col" className="num">
              Маш.-ч на час механизатора
            </th>
            <th scope="col" className="num" title="Доля ставки, которой оплачивается час простоя">
              Оплата простоя
            </th>
            <th scope="col" className="num">
              Моделей
            </th>
            <th scope="col" className="num">
              Единиц
            </th>
            <th scope="col">Должности Хронума</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <GroupRow key={g.id} group={g} onOpen={onOpen} />
          ))}
        </tbody>
      </Table>
    </ScrollRegion>
  );
}
